import {
  type Branch,
  DOCUMENT_KINDS,
  type ExpenseCategory,
  HACIENDA_STATUSES,
  RECEPTION_DECISIONS,
  REPORTS,
  type ReportBlock,
  type ReportColumn,
  type ReportKey,
  type ReportResult,
  type ReportRow,
} from '@facture/contracts'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  BarChart3Icon,
  BoxIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FileArchiveIcon,
  FileMinus2Icon,
  FilePlus2Icon,
  FileSpreadsheetIcon,
  FileTextIcon,
  InboxIcon,
  InfoIcon,
  type LucideIcon,
  PercentIcon,
  ReceiptIcon,
  UsersIcon,
  WalletIcon,
  WalletMinimalIcon,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useDebounced } from '@/components/ui/data-table'
import {
  ClearFilters,
  FilterBar,
  FilterDateRange,
  FilterSearch,
  FilterSelect,
} from '@/components/ui/filter-bar'
import { Page, PageHeader } from '@/components/ui/page'
import { Skeleton } from '@/components/ui/skeleton'
import { downloadFile } from '@/features/documents/api'
import { api, errorMessage } from '@/lib/api'
import { monthStartCR, nowCR, todayCR, yearRangeCR, yearStartCR } from '@/lib/dates'
import { formatDate, formatMoney, formatNumber } from '@/lib/format'
import { cn } from '@/lib/utils'

const ICONS: Record<ReportKey, LucideIcon> = {
  invoices: FileTextIcon,
  tickets: ReceiptIcon,
  notes: FileMinus2Icon,
  sales: FileTextIcon,
  tax: PercentIcon,
  purchases: InboxIcon,
  expenses: WalletMinimalIcon,
  products: BoxIcon,
  receivables: WalletIcon,
  customers: UsersIcon,
}

const searchSchema = z.object({
  report: z.enum(REPORTS.map((r) => r.key) as [ReportKey, ...ReportKey[]]).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  branchId: z.string().optional(),
  status: z.string().optional(),
  q: z.string().optional(),
  noteType: z.enum(['NC', 'ND']).optional(),
  kind: z.string().optional(),
  decision: z.enum(['pending', '1', '2', '3']).optional(),
  categoryId: z.string().optional(),
  page: z.number().int().min(1).optional(),
})
type Search = z.infer<typeof searchSchema>

/** Qué filtros acepta cada reporte (los demás se ignoran y no se envían). */
const FILTERS: Partial<
  Record<ReportKey, ('search' | 'status' | 'branch' | 'noteType' | 'kind' | 'decision' | 'category')[]>
> = {
  invoices: ['search', 'status', 'branch'],
  tickets: ['search', 'status', 'branch'],
  notes: ['search', 'status', 'branch', 'noteType'],
  sales: ['search', 'status', 'branch', 'kind'],
  tax: ['branch'],
  purchases: ['search', 'status', 'decision'],
  expenses: ['branch', 'category'],
}

/** Reportes con la descarga de XML y PDF del período. */
const WITH_ZIP: ReportKey[] = ['invoices', 'tickets', 'notes', 'sales']

export const Route = createFileRoute('/_app/reports')({
  staticData: { crumb: 'Reportes' },
  validateSearch: searchSchema,
  component: ReportsPage,
})

const MONEY_TYPES = ['money', 'number', 'percent']

function ReportsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const report = search.report ?? 'invoices'
  const allowed = FILTERS[report] ?? []
  const has = (f: NonNullable<(typeof FILTERS)[ReportKey]>[number]) => allowed.includes(f)
  const [term, setTerm] = useState(search.q ?? '')
  const q = useDebounced(term, 350)
  const filters: Record<string, string | undefined> = {
    ...(has('search') ? { search: q || undefined } : {}),
    ...(has('status') ? { status: search.status } : {}),
    ...(has('branch') ? { branchId: search.branchId } : {}),
    ...(has('noteType') ? { noteType: search.noteType } : {}),
    ...(has('kind') ? { kind: search.kind } : {}),
    ...(has('decision') ? { decision: search.decision } : {}),
    ...(has('category') ? { categoryId: search.categoryId } : {}),
  }
  const branches = useQuery({ queryKey: ['branches'], queryFn: () => api.get<Branch[]>('/branches') })
  const categories = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api.get<ExpenseCategory[]>('/expense-categories'),
    enabled: has('category'),
  })
  // Fechas en hora de Costa Rica (no la del navegador): el "hoy" y el "mes" coinciden con los de Hacienda.
  const from = search.from ?? (report === 'expenses' ? yearStartCR() : monthStartCR())
  const to = search.to ?? todayCR()
  const page = search.page ?? 1
  const [exporting, setExporting] = useState(false)
  const [zipping, setZipping] = useState(false)
  const setSearch = (patch: Partial<Search>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })
  /** Cambiar un filtro vuelve a la primera página. */
  const update = (patch: Partial<Search>) => setSearch({ ...patch, page: undefined })

  const data = useQuery({
    queryKey: ['reports', report, from, to, filters, page],
    queryFn: () => api.get<ReportResult>(`/reports/${report}`, { query: { from, to, ...filters, page } }),
    placeholderData: keepPreviousData,
  })

  const currentYear = nowCR().year
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i)
  const selectedYear = from.endsWith('-01-01') && from.slice(0, 4) === to.slice(0, 4) ? from.slice(0, 4) : ''

  /** ZIP con los XML firmados, las respuestas de Hacienda y los PDF del período. */
  async function exportZip() {
    setZipping(true)
    try {
      let skipped = 0
      await downloadFile(
        `/documents/export/files.zip?from=${from}&to=${to}&pdf=1`,
        `comprobantes-${from}-${to}.zip`,
        (response) => {
          skipped = Number(response.headers.get('X-Pdf-Skipped') ?? 0)
        },
      )
      if (skipped > 0) {
        toast.warning(
          `Se omitieron ${skipped} PDF por el límite de la descarga. El ZIP trae todos los XML y un LEEME.txt; descargá períodos más cortos para obtener el resto.`,
          { duration: 12_000 },
        )
      }
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setZipping(false)
    }
  }

  async function exportExcel() {
    setExporting(true)
    try {
      const extra = new URLSearchParams(
        Object.entries(filters).filter((e): e is [string, string] => !!e[1]),
      ).toString()
      await downloadFile(
        `/reports/${report}?from=${from}&to=${to}&format=xlsx${extra ? `&${extra}` : ''}`,
        `${report}-${from}-${to}.xlsx`,
      )
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setExporting(false)
    }
  }

  const result = data.data
  const pages = result ? Math.max(1, Math.ceil(result.rowCount / result.pageSize)) : 1

  return (
    <Page>
      <PageHeader
        title="Reportes"
        description="Información lista para tu contador y para la declaración de IVA. Todo exportable a Excel."
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[17rem_minmax(0,1fr)]">
        <nav className="grid content-start gap-1.5">
          {REPORTS.map((r) => {
            const Icon = ICONS[r.key]
            const active = r.key === report
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => setSearch({ report: r.key, page: undefined })}
                className={cn(
                  'flex items-start gap-3 rounded-xl border p-3 text-left transition',
                  active
                    ? 'border-primary/40 bg-card shadow-elevation-1 ring-2 ring-primary/10'
                    : 'border-transparent hover:bg-accent/60',
                )}
              >
                <span
                  className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-lg',
                    active ? 'bg-primary-soft text-primary' : 'bg-muted text-muted-foreground',
                  )}
                >
                  <Icon className="size-4" />
                </span>
                <span className="min-w-0">
                  <span className="block font-medium text-sm">{r.name}</span>
                  <span className="block text-muted-foreground text-xs leading-snug">{r.description}</span>
                </span>
              </button>
            )
          })}
        </nav>

        <Card className="min-w-0 overflow-hidden">
          <div className="flex flex-col gap-3 border-border border-b p-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="font-semibold">
                {result?.title ?? REPORTS.find((r) => r.key === report)?.name}
              </h2>
              <p className="text-muted-foreground text-xs">{result?.subtitle}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button loading={exporting} onClick={exportExcel}>
                <FileSpreadsheetIcon /> Excel
              </Button>
              {WITH_ZIP.includes(report) && (
                <Button variant="outline" loading={zipping} onClick={exportZip}>
                  <FileArchiveIcon /> XML y PDF
                </Button>
              )}
            </div>
          </div>
          <div className="border-border border-b bg-subtle/50 px-4 py-3">
            <FilterBar>
              {has('search') && (
                <FilterSearch
                  value={term}
                  onChange={(v) => {
                    setTerm(v)
                    setSearch({ q: v || undefined, page: undefined })
                  }}
                  placeholder={
                    report === 'purchases'
                      ? 'Buscar por proveedor, cédula o consecutivo…'
                      : 'Buscar por consecutivo, cliente o cédula…'
                  }
                />
              )}
              {has('kind') && (
                <FilterSelect
                  label="Tipo de comprobante"
                  width="xl"
                  value={search.kind ?? 'all'}
                  onValueChange={(v) => update({ kind: v === 'all' ? undefined : v })}
                  options={[
                    { value: 'all', label: 'Todos los tipos' },
                    ...DOCUMENT_KINDS.filter((k) => k.code !== 'FEC').map((k) => ({
                      value: k.code,
                      label: k.name,
                    })),
                  ]}
                />
              )}
              {has('noteType') && (
                <FilterSelect
                  label="Tipo de nota"
                  width="lg"
                  value={search.noteType ?? 'all'}
                  onValueChange={(v) => update({ noteType: v === 'all' ? undefined : (v as 'NC' | 'ND') })}
                  options={[
                    { value: 'all', label: 'Créditos y débitos' },
                    { value: 'NC', label: 'Solo notas de crédito' },
                    { value: 'ND', label: 'Solo notas de débito' },
                  ]}
                />
              )}
              {has('decision') && (
                <FilterSelect
                  label="Respuesta"
                  width="lg"
                  value={search.decision ?? 'all'}
                  onValueChange={(v) => update({ decision: v === 'all' ? undefined : (v as 'pending') })}
                  options={[
                    { value: 'all', label: 'Todas las respuestas' },
                    { value: 'pending', label: 'Pendientes' },
                    ...RECEPTION_DECISIONS.map((d) => ({ value: d.code, label: d.name })),
                  ]}
                />
              )}
              {has('status') && (
                <FilterSelect
                  label="Estado"
                  width="lg"
                  value={search.status ?? 'all'}
                  onValueChange={(v) => update({ status: v === 'all' ? undefined : v })}
                  options={[
                    { value: 'all', label: 'Todos los estados' },
                    ...HACIENDA_STATUSES.filter((s) => s.code !== 'draft').map((s) => ({
                      value: s.code,
                      label: s.name,
                    })),
                  ]}
                />
              )}
              {has('category') && (
                <FilterSelect
                  label="Categoría"
                  width="lg"
                  value={search.categoryId ?? 'all'}
                  onValueChange={(v) => update({ categoryId: v === 'all' ? undefined : v })}
                  options={[
                    { value: 'all', label: 'Todas las categorías' },
                    ...(categories.data ?? []).map((c) => ({ value: c.id, label: c.name })),
                  ]}
                />
              )}
              {has('branch') && (branches.data?.length ?? 0) > 1 && (
                <FilterSelect
                  label="Sucursal"
                  width="lg"
                  value={search.branchId ?? 'all'}
                  onValueChange={(v) => update({ branchId: v === 'all' ? undefined : v })}
                  options={[
                    { value: 'all', label: 'Todas las sucursales' },
                    ...(branches.data ?? []).map((b) => ({ value: b.id, label: `${b.code} · ${b.name}` })),
                  ]}
                />
              )}
              {report === 'expenses' && (
                <FilterSelect
                  label="Año"
                  width="sm"
                  value={selectedYear || 'custom'}
                  onValueChange={(v) => {
                    if (v === 'custom') return
                    const range = yearRangeCR(Number(v))
                    update({ from: range.from, to: range.to })
                  }}
                  options={[
                    ...(selectedYear ? [] : [{ value: 'custom', label: 'Año…', disabled: true }]),
                    ...years.map((y) => ({ value: String(y), label: String(y) })),
                  ]}
                />
              )}
              <FilterDateRange
                value={{ from, to }}
                clearable={false}
                onChange={(r) => update({ from: r.from, to: r.to })}
              />
              <ClearFilters
                active={
                  !!(
                    search.from ||
                    search.to ||
                    (has('search') && search.q) ||
                    (has('kind') && search.kind) ||
                    (has('noteType') && search.noteType) ||
                    (has('decision') && search.decision) ||
                    (has('status') && search.status) ||
                    (has('category') && search.categoryId) ||
                    (has('branch') && search.branchId)
                  )
                }
                onClear={() => {
                  setTerm('')
                  update({
                    from: undefined,
                    to: undefined,
                    q: undefined,
                    kind: undefined,
                    noteType: undefined,
                    decision: undefined,
                    status: undefined,
                    categoryId: undefined,
                    branchId: undefined,
                  })
                }}
              />
            </FilterBar>
          </div>
          {result?.notices?.map((notice) => (
            <p
              key={notice}
              className="flex items-start gap-2 border-border border-b bg-info-soft/40 px-4 py-2 text-info-soft-foreground text-xs"
            >
              <InfoIcon className="mt-0.5 size-3.5 shrink-0" /> {notice}
            </p>
          ))}
          {data.isLoading && (
            <div className="grid gap-2 p-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          )}
          {result?.blocks ? (
            <NoteBlocks result={result} />
          ) : (
            result && (
              <div className="relative max-h-[65dvh] overflow-auto">
                <ReportTable
                  columns={result.columns}
                  rows={result.rows}
                  totals={result.totals}
                  totalsLabel={result.totalsLabel}
                />
                {result.rows.length === 0 && <EmptyReport />}
              </div>
            )
          )}
          {result && result.rowCount > result.pageSize && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-border border-t bg-subtle px-4 py-2.5 text-muted-foreground text-xs">
              <span className="tabular">
                {formatNumber((result.page - 1) * result.pageSize + 1)}–
                {formatNumber(Math.min(result.rowCount, result.page * result.pageSize))} de{' '}
                {formatNumber(result.rowCount)} · los totales son del período completo
              </span>
              <div className="flex items-center gap-2">
                <span className="tabular">
                  Página {result.page} de {pages}
                </span>
                <Button
                  variant="outline"
                  size="icon-xs"
                  disabled={result.page <= 1}
                  onClick={() => setSearch({ page: result.page - 1 || undefined })}
                  aria-label="Página anterior"
                >
                  <ChevronLeftIcon />
                </Button>
                <Button
                  variant="outline"
                  size="icon-xs"
                  disabled={result.page >= pages}
                  onClick={() => setSearch({ page: result.page + 1 })}
                  aria-label="Página siguiente"
                >
                  <ChevronRightIcon />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Page>
  )
}

function EmptyReport() {
  return (
    <div className="flex flex-col items-center gap-2 py-16 text-center text-muted-foreground text-sm">
      <BarChart3Icon className="size-6" /> Sin datos en el período elegido.
    </div>
  )
}

function cellValue(value: string | number | null, type: string) {
  if (value === null || value === undefined || value === '')
    return <span className="text-muted-foreground">—</span>
  if (type === 'money') return formatMoney(Number(value))
  if (type === 'number') return formatNumber(Number(value))
  if (type === 'date') return formatDate(String(value))
  return String(value)
}

function ReportTable({
  columns,
  rows,
  totals,
  totalsLabel,
}: {
  columns: ReportColumn[]
  rows: ReportRow[]
  totals: Record<string, number> | null
  totalsLabel?: string
}) {
  return (
    <table className="w-full min-w-max text-sm">
      <thead className="sticky top-0 z-10 bg-subtle">
        <tr className="border-border border-b">
          {columns.map((c) => (
            <th
              key={c.key}
              className={cn(
                'h-9 whitespace-nowrap px-3 font-medium text-muted-foreground text-xs first:pl-4',
                MONEY_TYPES.includes(c.type) ? 'text-right' : 'text-left',
              )}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          // Filas de título (D-104): solo texto en la primera columna y sin importes.
          const heading = columns.slice(1).every((c) => row[c.key] === null || row[c.key] === undefined)
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: filas de reporte sin identidad propia
            <tr key={i} className="border-border border-b transition-colors hover:bg-accent/40">
              {columns.map((c, ci) => (
                <td
                  key={c.key}
                  className={cn(
                    'h-10 whitespace-nowrap px-3 first:pl-4',
                    MONEY_TYPES.includes(c.type) && 'text-right tabular',
                    heading && columns.length === 3 && ci === 0 && 'font-semibold text-xs tracking-wide',
                  )}
                >
                  {heading && columns.length === 3 && ci > 0 ? '' : cellValue(row[c.key] ?? null, c.type)}
                </td>
              ))}
            </tr>
          )
        })}
      </tbody>
      {totals && (
        <tfoot className="sticky bottom-0 bg-subtle font-semibold">
          <tr className="border-border border-t">
            {columns.map((c, i) => (
              <td
                key={c.key}
                className={cn(
                  'h-10 whitespace-nowrap px-3 first:pl-4',
                  MONEY_TYPES.includes(c.type) && 'text-right tabular',
                )}
              >
                {i === 0
                  ? (totalsLabel ?? 'Total')
                  : totals[c.key] !== undefined
                    ? cellValue(totals[c.key] ?? null, c.type)
                    : ''}
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  )
}

/** Notas de crédito y de débito en bloques separados, con su tarjeta de resumen y el neto. */
function NoteBlocks({ result }: { result: ReportResult }) {
  const net = result.totals?.total ?? 0
  return (
    <div className="grid gap-5 p-4">
      <div className="grid gap-3 sm:grid-cols-3">
        {result.blocks?.map((b) => (
          <SummaryTile key={b.key} block={b} />
        ))}
        <div className="rounded-xl border border-primary/25 bg-primary-soft/50 p-4">
          <div className="text-primary-soft-foreground text-xs">{result.totalsLabel ?? 'Neto'}</div>
          <div className="mt-1 font-semibold text-xl tabular">{formatMoney(net)}</div>
          <div className="text-muted-foreground text-xs">
            {formatNumber(result.rowCount)} notas en el período
          </div>
        </div>
      </div>
      {result.blocks?.map((b) => (
        <section key={b.key} className="overflow-hidden rounded-xl border border-border">
          <h3 className="flex items-center gap-2 border-border border-b bg-subtle px-4 py-2.5 font-semibold text-sm">
            {b.key === 'NC' ? (
              <FileMinus2Icon className="size-4 text-destructive" />
            ) : (
              <FilePlus2Icon className="size-4 text-success" />
            )}
            {b.title}
          </h3>
          <div className="max-h-[45dvh] overflow-auto">
            <ReportTable
              columns={result.columns}
              rows={b.rows}
              totals={b.totals}
              totalsLabel={`Total ${b.title.toLowerCase()}`}
            />
            {b.rows.length === 0 && <EmptyReport />}
          </div>
        </section>
      ))}
    </div>
  )
}

function SummaryTile({ block }: { block: ReportBlock }) {
  const credit = block.key === 'NC'
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-muted-foreground text-xs">
        {credit ? (
          <FileMinus2Icon className="size-3.5 text-destructive" />
        ) : (
          <FilePlus2Icon className="size-3.5 text-success" />
        )}
        {block.title}
      </div>
      <div className="mt-1 font-semibold text-xl tabular">{formatMoney(block.totals.total ?? 0)}</div>
      <div className="text-muted-foreground text-xs">
        {formatNumber(block.count)} nota{block.count === 1 ? '' : 's'} · IVA{' '}
        {formatMoney(block.totals.iva ?? 0)}
      </div>
    </div>
  )
}
